/**
 * Native-platform helpers.
 *
 * The web build already handles everything a driver needs (GPS via the
 * browser's Geolocation API, camera via ``<input type="file">``, offline via
 * the service worker). But when the same code runs inside the Capacitor
 * shell — i.e. the iOS App Store / Google Play Store build — we get better
 * hardware access:
 *
 *   • Native haptics (crisper than ``navigator.vibrate``)
 *   • True background GPS while the phone screen is off
 *   • Full-resolution camera without Safari's tap-through
 *   • System-level permission dialogs (better UX than browser prompts)
 *
 * This helper detects whether we're running in the native shell and exposes
 * a thin adapter around each API. The web code path is untouched so nothing
 * else changes when we're in a browser.
 */
import { Capacitor } from '@capacitor/core';

export const isNative = () => Capacitor.isNativePlatform();
export const nativePlatform = () => Capacitor.getPlatform();

/** Native haptic pulse. Falls back to Web Vibration API. */
export async function haptic(pattern = 'medium') {
    if (isNative()) {
        try {
            const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
            if (pattern === 'arrival') {
                await Haptics.impact({ style: ImpactStyle.Heavy });
                setTimeout(() => Haptics.impact({ style: ImpactStyle.Medium }), 180);
            } else {
                const style = pattern === 'light' ? ImpactStyle.Light
                            : pattern === 'heavy' ? ImpactStyle.Heavy
                            : ImpactStyle.Medium;
                await Haptics.impact({ style });
            }
            return;
        } catch { /* fall through to web */ }
    }
    try { navigator.vibrate?.(pattern === 'arrival' ? [180, 90, 180] : [80]); } catch { /* silent */ }
}

/** Wrap the browser Geolocation API so both native + web behave the same. */
export async function watchPosition(cb) {
    if (isNative()) {
        try {
            const { Geolocation } = await import('@capacitor/geolocation');
            const id = await Geolocation.watchPosition(
                { enableHighAccuracy: true, timeout: 10000 },
                (pos) => {
                    if (!pos) return;
                    cb({
                        lat: pos.coords.latitude,
                        lng: pos.coords.longitude,
                        heading: pos.coords.heading ?? undefined,
                        speed: pos.coords.speed ?? undefined,
                        accuracy: pos.coords.accuracy,
                    });
                },
            );
            return () => Geolocation.clearWatch({ id });
        } catch { /* fall through */ }
    }
    if (!navigator.geolocation) return () => {};
    const id = navigator.geolocation.watchPosition(
        (pos) => cb({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            heading: pos.coords.heading ?? undefined,
            speed: pos.coords.speed ?? undefined,
            accuracy: pos.coords.accuracy,
        }),
        () => {},
        { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 },
    );
    return () => navigator.geolocation.clearWatch(id);
}

/** True online/offline status. Native gives us cellular vs wifi distinction. */
export async function subscribeNetwork(cb) {
    if (isNative()) {
        try {
            const { Network } = await import('@capacitor/network');
            const status = await Network.getStatus();
            cb({ online: status.connected, type: status.connectionType });
            const handle = await Network.addListener('networkStatusChange',
                (s) => cb({ online: s.connected, type: s.connectionType }));
            return () => handle.remove();
        } catch { /* fall through */ }
    }
    const onOnline = () => cb({ online: true });
    const onOffline = () => cb({ online: false });
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    cb({ online: navigator.onLine });
    return () => {
        window.removeEventListener('online', onOnline);
        window.removeEventListener('offline', onOffline);
    };
}
